import { useContext, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MoreVertical } from "lucide-react";
import { Dropdown } from "react-bootstrap";
import { createPortal } from "react-dom";
import RunDetails from "./RunDetails";
import Heading from "../../../shared_components/Heading/Heading";
import PaginationGroup from "../../../shared_components/PaginationGroup/PaginationGroup";
import UserData from "../../../services/UserData";
import AIPreEnrichmentService from "../../../services/AIPreEnrichmentService";
import layout from "../IngestionBatches/IngestionBatches.module.css";
import style from "./PreEnrichmentRuns.module.css";

const decisions = ["PENDING", "APPROVED", "REJECTED", "HUMAN_REVIEW_REQUIRED"];

const RunDate = ({ value }) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "—";
  return <time dateTime={value} title={value}>{date.toLocaleString("en-GB")}</time>;
};

const PreEnrichmentRuns = () => {
  const userData = useContext(UserData);
  const location = useLocation();
  const [page, setPage] = useState(location.state?.page || 1);
  const [pageSize, setPageSize] = useState(location.state?.pageSize || 10);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [selectedRunId, setSelectedRunId] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setData(null);
    if (!userData) return () => { active = false; };
    AIPreEnrichmentService.getRuns(userData, page, pageSize)
      .then(result => { if (active) setData(result); })
      .catch(() => { if (active) setError("Unable to load pre-enrichment runs. Please try again."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [userData, page, pageSize, refresh]);

  return (
    <section className={layout.container} aria-label="Pre-Enrichment Runs" aria-busy={loading}>
      <Heading>Pre-Enrichment Runs</Heading>
      <div className={layout.loadingSlot}>
        {loading && <div className={layout.loadingBar} role="progressbar" aria-label="Loading pre-enrichment runs" />}
      </div>
      <div className={layout.content}>
        {error && <div role="alert">{error} <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setRefresh(value => value + 1)}>Retry</button></div>}
        {data && (
          <table className={style.table}>
            <caption>Decision counts are proposed outcomes reported by each run.</caption>
            <thead>
              <tr>
                <th scope="col">Started at</th>
                <th scope="col">Mode</th>
                <th scope="col">Status</th>
                <th scope="col">Ruleset</th>
                {decisions.map(decision => <th scope="col" key={decision}>{decision.replaceAll("_", " ")}</th>)}
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.runs.map(run => (
                <tr key={run.id}>
                  <td><RunDate value={run.started_at} /></td>
                  <td>{run.mode ?? "—"}</td>
                  <td>{run.status ?? "—"}</td>
                  <td className={style.ruleset}>
                    <div>{run.report?.ruleset_id ?? "—"}</div>
                    {run.report?.ruleset_version && <div>Version {run.report.ruleset_version}</div>}                   
                  </td>
                  {decisions.map(decision => <td className={style.count} key={decision}>{run.report?.proposed ? (run.report.proposed[decision] ?? 0).toLocaleString("en-GB") : "—"}</td>)}
                  <td>
                    <Dropdown>
                      <Dropdown.Toggle variant="light" className={style.actionsToggle} id={`run-actions-${run.id}`} aria-label={`Actions for run ${run.id}`}>
                        <MoreVertical size={22} aria-hidden="true" />
                      </Dropdown.Toggle>
                      {createPortal(
                        <Dropdown.Menu popperConfig={{ strategy: "fixed" }}>
                          <Dropdown.Item onClick={() => setSelectedRunId(run.id)}>View Details</Dropdown.Item>
                          <Dropdown.Item as={Link} to={`/intelligence/pre-enrichment-runs/${encodeURIComponent(run.id)}/documents`} state={{ page, pageSize }}>View Documents</Dropdown.Item>
                        </Dropdown.Menu>, document.body
                      )}
                    </Dropdown>
                  </td>
                </tr>
              ))}
              {data.runs.length === 0 && <tr><td colSpan={9}>No pre-enrichment runs found.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
      {!error && data && (
        <div className={layout.pagination}>
          <PaginationGroup activePage={page} numberOfPages={Math.max(1, data.pagination.total_pages)} updatePageClicked={setPage} />
          <label className={layout.pageSize}>
            Page size
            <select className="form-select form-select-sm" value={pageSize} onChange={event => {
              setPageSize(Number(event.target.value));
              setPage(1);
            }}>
              {[10, 25, 50, 100].map(size => <option key={size} value={size}>{size}</option>)}
            </select>
          </label>
        </div>
      )}
      {selectedRunId && <RunDetails key={selectedRunId} runId={selectedRunId} onClose={() => setSelectedRunId(null)} />}
    </section>
  );
};

export default PreEnrichmentRuns;
